/**
 * Date helpers.
 *
 * Dates are handled throughout as "date keys": plain `YYYY-MM-DD` strings in the
 * device's local time. Working in strings rather than Date objects avoids the
 * usual timezone drift, where a date built at midnight UTC lands on the previous
 * day in British Summer Time.
 *
 * Display format is British: DD MMM YYYY. Weeks run Monday to Sunday.
 */

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const WEEKDAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
export const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n) => String(n).padStart(2, '0');

/** Date key for a JavaScript Date, using its local calendar date. */
export function toKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midday Date for a date key. Midday keeps DST changes from shifting the day. */
export function toDate(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** Today's date key. */
export function today() {
  return toKey(new Date());
}

export function isValidKey(key) {
  return typeof key === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(key) && !Number.isNaN(toDate(key).getTime());
}

/** Shift a date key by a number of days (negative goes back). */
export function addDays(key, days) {
  const d = toDate(key);
  d.setDate(d.getDate() + days);
  return toKey(d);
}

export function addMonths(monthKey, months) {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 1 + months, 1, 12);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndex(key) {
  return (toDate(key).getDay() + 6) % 7;
}

export function isWeekend(key) {
  return weekdayIndex(key) >= 5;
}

/** The Monday of the week containing `key`. */
export function startOfWeek(key) {
  return addDays(key, -weekdayIndex(key));
}

/** The seven date keys, Monday to Sunday, of the week containing `key`. */
export function weekDays(key) {
  const monday = startOfWeek(key);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** `YYYY-MM` for a date key. */
export function monthOf(key) {
  return key.slice(0, 7);
}

/** Every date key in a `YYYY-MM` month. */
export function monthDays(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  const count = new Date(y, m, 0).getDate();
  return Array.from({ length: count }, (_, i) => `${monthKey}-${pad(i + 1)}`);
}

export function firstOfMonth(monthKey) {
  return `${monthKey}-01`;
}

export function lastOfMonth(monthKey) {
  const days = monthDays(monthKey);
  return days[days.length - 1];
}

/** "02 Oct 2026" */
export function formatDate(key) {
  const d = toDate(key);
  return `${pad(d.getDate())} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Friday 02 Oct 2026" */
export function formatDateLong(key) {
  return `${WEEKDAYS[weekdayIndex(key)]} ${formatDate(key)}`;
}

/** "Fri 02 Oct" */
export function formatDateShort(key) {
  const d = toDate(key);
  return `${WEEKDAYS_SHORT[weekdayIndex(key)]} ${pad(d.getDate())} ${MONTHS_SHORT[d.getMonth()]}`;
}

/** "October 2026" */
export function formatMonth(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

/** Whole days from `from` to `to`; negative if `to` is earlier. */
export function daysBetween(from, to) {
  const ms = toDate(to).getTime() - toDate(from).getTime();
  return Math.round(ms / 86400000);
}

/** "today", "yesterday", "3 days ago", "in 2 days" — used for gentle hints. */
export function relativeDays(key, reference = today()) {
  const diff = daysBetween(reference, key);
  if (diff === 0) return 'today';
  if (diff === -1) return 'yesterday';
  if (diff === 1) return 'tomorrow';
  if (diff < 0) return `${Math.abs(diff)} days ago`;
  return `in ${diff} days`;
}
